import React, { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { deleteUserFavorite } from '../../../../store/userAccount/userAccount.actions'
import {
  selectUserFavorites,
  selectUserFavoritesLimitNotReached
} from '../../../../store/userAccount/userAccount.selectors'
import RectangleActionButton, {
  COLOR_GREEN_EMPTY,
  SIZE_NORMAL
} from '../../../genericComponents/buttons/RectangleActionButton'
import { useAction } from '../../../utils/react-redux'
import UserScreenContent from '../../shared/UserScreenContent'
import UserScreenHeader from '../../shared/UserScreenHeader'
import UserFavoriteItem from './UserFavoriteItem'
import { setUserUIScreen } from '../../../../store/ui/ui.actions'
import { USER_SCREENS } from '../../../../store/ui/ui.constants'
import { FAVORITE_TYPES, HOME_ICON, WORK_ICON } from '../../UserAccount.constants'
import { useT } from '../../../i18n/T'

import UserFavoriteError from './UserFavoriteError'
import { ACTION, TYPE } from '../../../analytics/ClickTagsService'
import { TAG_CLICK_USER_FAVORITES_LIST } from '../../../../store/analytics/analytics.actionTypes'
import analyticsConstants from '../../../analytics/analytics.constants'

const buildTagData = (type, action) => ({
  type: TAG_CLICK_USER_FAVORITES_LIST,
  tagInfo: { [ACTION]: action, [TYPE]: type }
})

const UserFavorites = () => {
  const { home, work, others } = useSelector(selectUserFavorites) || {}
  const execDeleteFavorite = useAction(deleteUserFavorite)
  const execSetUserUIScreen = useAction(setUserUIScreen)

  const manageFavorite = type => execSetUserUIScreen(USER_SCREENS.manageFavorite, { type })
  const deleteFavorite = uuid => execDeleteFavorite({ uuid })

  const canStillAddFavorite = useSelector(selectUserFavoritesLimitNotReached)

  const renderAddBtn = useCallback(() => {
    if (canStillAddFavorite) {
      return (
        <RectangleActionButton
          label="Ajouter"
          color={COLOR_GREEN_EMPTY}
          size={SIZE_NORMAL}
          onClick={() => manageFavorite(FAVORITE_TYPES.OTHER)}
          tagData={buildTagData(
            analyticsConstants.user.favorites.type.other,
            analyticsConstants.user.favorites.action.listAdd
          )}
        />
      )
    }
  }, [manageFavorite])

  return (
    <>
      <UserScreenHeader rightChildren={renderAddBtn()}>{'Mes lieux favoris'}</UserScreenHeader>
      <UserFavoriteError marginTop={-10} />
      <UserScreenContent>
        <UserFavoriteItem
          key={home?.uuid}
          address={useT('useraccount.favorite.type-your-address')} // that prop will be overrided by `...home` if favorite is set
          favIcon={HOME_ICON} // that prop will be overrided but it’s usefull when no favorite exist
          {...home}
          action={() => manageFavorite(FAVORITE_TYPES.HOME)}
          label={useT('useraccount.favorite.home')}
          actionIcon={'crayon-v6'}
          allowEmpty
          editTagData={buildTagData(
            analyticsConstants.user.favorites.type.home,
            analyticsConstants.user.favorites.action.listEdit
          )}
          gotoTagData={buildTagData(
            analyticsConstants.user.favorites.type.home,
            analyticsConstants.user.favorites.action.listView
          )}
        />
        <UserFavoriteItem
          key={work?.uuid}
          address={useT('useraccount.favorite.type-your-address')} // that prop will be overrided by `...work` if favorite is set
          favIcon={WORK_ICON} // that prop will be overrided but it’s usefull when no favorite exist
          {...work}
          action={() => manageFavorite(FAVORITE_TYPES.WORK)}
          label={useT('useraccount.favorite.work')}
          actionIcon={'crayon-v6'}
          allowEmpty
          editTagData={buildTagData(
            analyticsConstants.user.favorites.type.work,
            analyticsConstants.user.favorites.action.listEdit
          )}
          gotoTagData={buildTagData(
            analyticsConstants.user.favorites.type.work,
            analyticsConstants.user.favorites.action.listView
          )}
        />
        {(others || []).map((other, idx) => (
          <UserFavoriteItem
            key={other.uuid || idx}
            {...other}
            action={deleteFavorite}
            actionIcon={'poubelle'}
            editTagData={buildTagData(
              analyticsConstants.user.favorites.type.other,
              analyticsConstants.user.favorites.action.listDelete
            )}
            gotoTagData={buildTagData(
              analyticsConstants.user.favorites.type.other,
              analyticsConstants.user.favorites.action.listView
            )}
          />
        ))}
      </UserScreenContent>
    </>
  )
}

export default UserFavorites
